/**
 * Store de estado para carpetas
 * @module stores/useFolderStore
 */

import { create } from 'zustand'
import { folderServiceApi, Folder } from '@/services/folderServiceApi'
import { UNFILED_FOLDER_SENTINEL } from '@/constants/folderFilter'

// ============================================================================
// TYPES
// ============================================================================

interface FolderStore {
  // Estado
  folders: Folder[]
  folderTree: Folder[]
  activeFolderId: string | null
  loading: boolean
  error: string | null

  // Acciones - Carpetas
  init: () => Promise<void>
  loadFolders: () => Promise<void>
  loadFolderTree: () => Promise<void>
  createFolder: (name: string, description?: string, parentId?: string | null) => Promise<Folder>
  updateFolder: (id: string, data: { name?: string; description?: string | null }) => Promise<void>
  deleteFolder: (id: string) => Promise<void>
  setActiveFolder: (id: string | null) => void
  getActiveFolder: () => Folder | undefined
  getFolderById: (id: string) => Folder | undefined
  getRootFolders: () => Folder[]
  getChildren: (parentId: string) => Folder[]

  // Acciones - Nodos en carpetas
  addNodeToFolder: (folderId: string, nodeId: string) => Promise<void>
  removeNodeFromFolder: (folderId: string, nodeId: string) => Promise<void>
  moveNodes: (sourceFolderId: string, targetFolderId: string, nodeIds: string[]) => Promise<void>
  assignNodesToFolder: (folderId: string, nodeIds: string[]) => Promise<number>

  // Utilidades
  clearError: () => void
}

// ============================================================================
// STORE
// ============================================================================

export const useFolderStore = create<FolderStore>((set, get) => ({
  // Estado inicial
  folders: [],
  folderTree: [],
  activeFolderId: null,
  loading: false,
  error: null,

  // Inicializar
  init: async () => {
    set({ loading: true, error: null })
    try {
      await Promise.all([
        get().loadFolders(),
        get().loadFolderTree()
      ])
      set({ loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error inicializando carpetas',
        loading: false,
      })
    }
  },

  // Cargar todas las carpetas
  loadFolders: async () => {
    set({ loading: true, error: null })
    try {
      const folders = await folderServiceApi.getAll()
      set({ folders, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error cargando carpetas',
        loading: false,
      })
    }
  },

  // Cargar árbol de carpetas
  loadFolderTree: async () => {
    try {
      const folderTree = await folderServiceApi.getTree()
      set({ folderTree })
    } catch (error) {
      console.error('Error loading folder tree:', error)
    }
  },

  // Crear carpeta
  createFolder: async (name: string, description?: string, parentId?: string | null) => {
    set({ loading: true, error: null })
    try {
      const newFolder = await folderServiceApi.create({ name, description, parentId })
      set((state) => ({
        folders: [...state.folders, newFolder],
        loading: false,
      }))
      // Recargar árbol
      get().loadFolderTree()
      return newFolder
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error creando carpeta',
        loading: false,
      })
      throw error
    }
  },

  // Actualizar carpeta
  updateFolder: async (id: string, data: { name?: string; description?: string | null }) => {
    set({ loading: true, error: null })
    try {
      const updatedFolder = await folderServiceApi.update(id, data)
      set((state) => ({
        folders: state.folders.map((f) => (f.id === id ? updatedFolder : f)),
        loading: false,
      }))
      get().loadFolderTree()
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error actualizando carpeta',
        loading: false,
      })
      throw error
    }
  },

  // Eliminar carpeta
  deleteFolder: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await folderServiceApi.delete(id)
      set((state) => ({
        folders: state.folders.filter((f) => f.id !== id),
        activeFolderId: state.activeFolderId === id ? null : state.activeFolderId,
        loading: false,
      }))
      get().loadFolderTree()
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error eliminando carpeta',
        loading: false,
      })
      throw error
    }
  },

  // Seleccionar carpeta activa
  setActiveFolder: (id: string | null) => {
    set({ activeFolderId: id })
  },

  // Obtener carpeta activa (la vista "sin carpeta" no es una carpeta real)
  getActiveFolder: () => {
    const { activeFolderId, folders } = get()
    if (!activeFolderId || activeFolderId === UNFILED_FOLDER_SENTINEL) return undefined
    return folders.find((f) => f.id === activeFolderId)
  },

  // Obtener carpeta por ID
  getFolderById: (id: string) => {
    return get().folders.find((f) => f.id === id)
  },

  // Obtener carpetas raíz
  getRootFolders: () => {
    return get().folders.filter((f) => !f.parentId)
  },

  // Obtener carpetas hijas
  getChildren: (parentId: string) => {
    return get().folders.filter((f) => f.parentId === parentId)
  },

  // Agregar nodo a carpeta
  addNodeToFolder: async (folderId: string, nodeId: string) => {
    try {
      await folderServiceApi.addNodeToFolder(folderId, nodeId)
      await get().loadFolders()
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error agregando nodo a carpeta',
      })
      throw error
    }
  },

  // Remover nodo de carpeta
  removeNodeFromFolder: async (folderId: string, nodeId: string) => {
    try {
      await folderServiceApi.removeNodeFromFolder(folderId, nodeId)
      await get().loadFolders()
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error removiendo nodo de carpeta',
      })
      throw error
    }
  },

  // Mover nodos entre carpetas
  moveNodes: async (sourceFolderId: string, targetFolderId: string, nodeIds: string[]) => {
    try {
      await folderServiceApi.moveNodes(sourceFolderId, targetFolderId, nodeIds)
      await get().loadFolders()
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error moviendo nodos',
      })
      throw error
    }
  },

  assignNodesToFolder: async (folderId: string, nodeIds: string[]) => {
    if (nodeIds.length === 0) return 0
    try {
      const { updated } = await folderServiceApi.assignNodesBatch(folderId, nodeIds)
      await get().loadFolders()
      await get().loadFolderTree()
      return updated
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Error asignando nodos a carpeta',
      })
      throw error
    }
  },

  // Limpiar error
  clearError: () => {
    set({ error: null })
  },
}))
